/* ═══════════════════════════════════════════════
   STASH HOME FURNITURE — interactions
   GSAP + ScrollTrigger + SplitText · Lenis · Three.js
   ═══════════════════════════════════════════════ */

gsap.registerPlugin(ScrollTrigger, SplitText);

const isDesktop = window.matchMedia("(min-width: 901px)").matches;
const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ── Lenis smooth scroll ── */
/* Native scrolling only — the Lenis smooth-scroll layer caused pin
   release lag on the collections rail (stuck-overlay flashes when
   scrolling back up). ScrollTrigger pins are exact with native scroll. */
const lenis = null;

/* ── Hero video: keep it playing (Chrome pauses offscreen autoplay videos
   and doesn't always resume them after fast scrolling) ── */
(function () {
  var heroVideo = document.querySelector(".hero__video");
  if (!heroVideo) return;
  function ensurePlay() {
    if (heroVideo.paused) {
      var p = heroVideo.play();
      if (p && p.catch) p.catch(function () {});
    }
  }
  if (window.IntersectionObserver) {
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) ensurePlay(); });
    }, { threshold: 0.05 }).observe(heroVideo);
  }
  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) ensurePlay();
  });
  window.addEventListener("pageshow", ensurePlay);
  window.addEventListener("scroll", function () {
    if (heroVideo.getBoundingClientRect().bottom > 0) ensurePlay();
  }, { passive: true });
})();

/* ── Three.js hero: brass diamond constellation ── */
(function initHero() {
  const canvas = document.getElementById("heroCanvas");
  if (!canvas || !window.THREE) return;

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x161512, 0.055);

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
  camera.position.set(0, 0, 9);

  const renderer = new THREE.WebGLRenderer({
    canvas, alpha: true, antialias: true,
    powerPreference: "low-power"
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const brass = new THREE.Color(0xa67c4e);
  const sage = new THREE.Color(0x8a8a5c);

  // Central wireframe diamond (octahedron) — the brand glyph
  const diamondGroup = new THREE.Group();
  const geo = new THREE.OctahedronGeometry(2.1, 0);
  const wire = new THREE.LineSegments(
    new THREE.EdgesGeometry(geo),
    new THREE.LineBasicMaterial({ color: brass, transparent: true, opacity: 0.85 })
  );
  diamondGroup.add(wire);

  const inner = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.OctahedronGeometry(1.35, 0)),
    new THREE.LineBasicMaterial({ color: sage, transparent: true, opacity: 0.35 })
  );
  inner.rotation.y = Math.PI / 4;
  diamondGroup.add(inner);

  const core = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.16, 0),
    new THREE.MeshBasicMaterial({ color: brass })
  );
  diamondGroup.add(core);
  diamondGroup.position.x = isDesktop ? 2.8 : 0;
  diamondGroup.position.y = isDesktop ? 0 : 1.6;
  if (!isDesktop) {
    diamondGroup.scale.setScalar(0.82);
    wire.material.opacity = 0.55;
  }
  scene.add(diamondGroup);

  // Dust particles
  const COUNT = isDesktop ? 900 : 350;
  const positions = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 26;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 16;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 14;
  }
  const pGeo = new THREE.BufferGeometry();
  pGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const particles = new THREE.Points(pGeo, new THREE.PointsMaterial({
    color: brass, size: 0.035, transparent: true, opacity: 0.55,
    sizeAttenuation: true, depthWrite: false
  }));
  scene.add(particles);

  // Mouse parallax
  let mx = 0, my = 0, tx = 0, ty = 0;
  if (isDesktop) {
    window.addEventListener("pointermove", (e) => {
      tx = (e.clientX / window.innerWidth - 0.5) * 2;
      ty = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
  }

  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== w || canvas.height !== h) {
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
  }
  window.addEventListener("resize", resize, { passive: true });

  const clock = new THREE.Clock();
  let heroVisible = true;
  new IntersectionObserver(([entry]) => { heroVisible = entry.isIntersecting; })
    .observe(canvas);

  function tick() {
    requestAnimationFrame(tick);
    if (!heroVisible) return;
    resize();
    const t = clock.getElapsedTime();
    mx += (tx - mx) * 0.04;
    my += (ty - my) * 0.04;

    diamondGroup.rotation.y = t * 0.22 + mx * 0.35;
    diamondGroup.rotation.x = Math.sin(t * 0.18) * 0.12 + my * 0.2;
    diamondGroup.position.y = (isDesktop ? 0 : 1.6) + Math.sin(t * 0.7) * 0.18;
    inner.rotation.y = -t * 0.3;
    inner.rotation.z = t * 0.12;
    core.rotation.y = t * 0.6;

    particles.rotation.y = t * 0.016 + mx * 0.05;
    particles.position.y = Math.sin(t * 0.25) * 0.3;

    renderer.render(scene, camera);
  }
  if (!prefersReduced) tick();
  else { resize(); renderer.render(scene, camera); }
})();

/* ── Preloader → hero intro ──
   Full count-up only on the first visit of the session; near-instant after. */
window.addEventListener("load", () => {
  const pre = document.getElementById("preloader");
  const count = document.getElementById("preCount");
  const counter = { v: 0 };
  let seenIntro = false;
  try {
    seenIntro = !!sessionStorage.getItem("stash-intro");
    sessionStorage.setItem("stash-intro", "1");
  } catch (e) { /* private mode */ }

  const intro = gsap.timeline({ paused: true });
  intro
    .from(".hero__line", {
      yPercent: 110, duration: 1.2, stagger: 0.14, ease: "power4.out"
    })
    .from(".hero__eyebrow, .hero__sub, .hero__actions, .hero__foot", {
      y: 26, autoAlpha: 0, duration: 0.9, stagger: 0.09, ease: "power3.out"
    }, "-=0.7")
    .from(".nav", { y: -30, autoAlpha: 0, duration: 0.8, ease: "power3.out" }, "-=0.8");

  const fast = prefersReduced || seenIntro;
  gsap.timeline()
    .to(counter, {
      v: 100, duration: fast ? 0 : 0.7, ease: "power2.inOut",
      onUpdate: () => { count.textContent = String(Math.round(counter.v)).padStart(2, "0"); }
    })
    .to(pre, {
      yPercent: -100, duration: fast ? 0.35 : 0.7, ease: "power4.inOut",
      onStart: () => intro.play(),
      onComplete: () => pre.remove()
    }, fast ? "+=0" : "+=0.1");
});

/* ── Marquee ── */
if (!prefersReduced) {
  gsap.to("#marqueeTrack", { xPercent: -50, duration: 28, ease: "none", repeat: -1 });
}

/* ── Manifesto word-by-word scrub reveal ── */
(function manifesto() {
  const el = document.getElementById("manifestoText");
  if (!el) return;
  const split = new SplitText(el, { type: "words", wordsClass: "w" });
  gsap.to(split.words, {
    opacity: 1,
    stagger: 0.06,
    ease: "none",
    scrollTrigger: {
      trigger: el,
      start: "top 78%",
      end: "bottom 45%",
      scrub: true
    }
  });
})();

/* ── Collections: pinned horizontal scroll (desktop only) ── */
if (isDesktop && !prefersReduced) {
  const track = document.getElementById("collectionsTrack");
  const pin = document.getElementById("collectionsPin");
  const getDistance = () => track.scrollWidth - window.innerWidth;
  /* Trackpad horizontal swipes should drive the rail too — map deltaX
     into vertical scroll while the section is pinned. */
  pin.addEventListener("wheel", (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      window.scrollBy(0, e.deltaX);
      e.preventDefault();
    }
  }, { passive: false });
  gsap.to(track, {
    x: () => -getDistance(),
    ease: "none",
    scrollTrigger: {
      trigger: pin,
      start: "top top",
      end: () => "+=" + getDistance(),
      scrub: 0.6,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true
    }
  });
}

/* ── Generic section reveals ── */
gsap.utils.toArray([
  ".collections__head", ".edit__head", ".story__inner > *:not(.story__values)",
  ".visit__left > *", ".visit__row", ".footer__cols .footer__col"
]).forEach((el) => {
  gsap.from(el, {
    y: 40, autoAlpha: 0, duration: 1, ease: "power3.out",
    scrollTrigger: { trigger: el, start: "top 88%" }
  });
});

gsap.utils.toArray(".pcard, .vcard").forEach((el, i) => {
  gsap.from(el, {
    y: 56, autoAlpha: 0, duration: 1, delay: (i % 3) * 0.08, ease: "power3.out",
    scrollTrigger: { trigger: el, start: "top 92%" }
  });
});

/* Footer wordmark rise */
gsap.from("#footerMark", {
  y: 80, autoAlpha: 0, duration: 1.3, ease: "power3.out",
  scrollTrigger: { trigger: ".footer", start: "top 75%" }
});

/* ── Nav scrolled state ── */
const nav = document.getElementById("nav");
ScrollTrigger.create({
  start: 80,
  onUpdate: (self) => nav.classList.toggle("is-scrolled", self.scroll() > 80)
});

/* ── Mobile menu ── */
const burger = document.getElementById("burger");
const mmenu = document.getElementById("mmenu");
burger.addEventListener("click", () => {
  const open = mmenu.classList.toggle("is-open");
  burger.classList.toggle("is-open", open);
  burger.setAttribute("aria-expanded", open);
  mmenu.setAttribute("aria-hidden", !open);
  if (lenis) open ? lenis.stop() : lenis.start();
  document.body.style.overflow = open ? "hidden" : "";
});
mmenu.querySelectorAll("a").forEach((a) =>
  a.addEventListener("click", () => burger.click())
);

/* ── Anchor links through Lenis ── */
document.querySelectorAll('a[href^="#"]').forEach((a) => {
  a.addEventListener("click", (e) => {
    const target = document.querySelector(a.getAttribute("href"));
    if (!target) return;
    e.preventDefault();
    if (lenis) lenis.scrollTo(target, { offset: 0, duration: 1.4 });
    else target.scrollIntoView({ behavior: "smooth" });
  });
});

/* ── Search overlay (#StashSearch) ──
   The nav magnifier opens an in-page predictive-search overlay instead of
   navigating away. Plain class toggles + fetch only — no GSAP — so it works
   even when animations are suspended. href="/search" stays as no-JS fallback. */
(function stashSearch() {
  var trigger = document.querySelector(".nav__search");
  var overlay = document.getElementById("StashSearch");
  var input = document.getElementById("StashSearchInput");
  var results = document.getElementById("StashSearchResults");
  var closeBtn = document.getElementById("StashSearchClose");
  if (!trigger || !overlay || !input || !results) return;

  var debounceTimer = null;
  var controller = null;

  function openOverlay() {
    overlay.classList.add("is-open");
    overlay.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    setTimeout(function () {
      input.focus({ preventScroll: true });
      input.select();
    }, 40);
  }
  function closeOverlay() {
    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (debounceTimer) clearTimeout(debounceTimer);
    if (controller) controller.abort();
  }

  trigger.addEventListener("click", function (e) {
    e.preventDefault();
    openOverlay();
  });
  closeBtn.addEventListener("click", closeOverlay);
  overlay.addEventListener("click", function (e) {
    if (e.target === overlay) closeOverlay(); // backdrop only, not the panel
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && overlay.classList.contains("is-open")) closeOverlay();
  });

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function fmtPrice(p) {
    if (p == null || p === "") return "";
    p = String(p);
    return p.indexOf("$") !== -1 ? p : "$" + p;
  }
  function thumbUrl(u) {
    return u + (u.indexOf("?") > -1 ? "&" : "?") + "width=128";
  }

  function render(data, q) {
    results.innerHTML = "";
    var res = (data && data.resources && data.resources.results) || {};
    var products = res.products || [];
    var collections = res.collections || [];

    if (!products.length && !collections.length) {
      results.appendChild(el("p", "ssrch__empty", "Nothing yet — keep typing."));
      return;
    }

    if (products.length) {
      var list = el("div", "ssrch__list");
      products.forEach(function (p) {
        var row = el("a", "ssrch__row");
        row.href = p.url;
        var thumb = el("span", "ssrch__thumb");
        if (p.featured_image && p.featured_image.url) {
          var img = document.createElement("img");
          img.src = thumbUrl(p.featured_image.url);
          img.alt = "";
          img.loading = "lazy";
          img.width = 64;
          img.height = 64;
          thumb.appendChild(img);
        }
        row.appendChild(thumb);
        var meta = el("span", "ssrch__meta");
        meta.appendChild(el("span", "ssrch__title", p.title));
        var price = fmtPrice(p.price);
        if (price) meta.appendChild(el("span", "ssrch__price", price));
        row.appendChild(meta);
        list.appendChild(row);
      });
      results.appendChild(list);
    }

    if (collections.length) {
      results.appendChild(el("p", "ssrch__label", "Collections"));
      var clist = el("div", "ssrch__collections");
      collections.forEach(function (c) {
        var a = el("a", "ssrch__col", c.title);
        a.href = c.url;
        clist.appendChild(a);
      });
      results.appendChild(clist);
    }

    var all = el("a", "ssrch__all", "View all results →");
    all.href = "/search?q=" + encodeURIComponent(q);
    results.appendChild(all);
  }

  input.addEventListener("input", function () {
    var q = input.value.trim();
    if (debounceTimer) clearTimeout(debounceTimer);
    if (controller) controller.abort();
    if (q.length < 2) {
      results.innerHTML = "";
      return;
    }
    debounceTimer = setTimeout(function () {
      controller = new AbortController();
      var url =
        "/search/suggest.json?q=" + encodeURIComponent(q) +
        "&resources[type]=product,collection" +
        "&resources[limit]=8" +
        "&resources[options][unavailable_products]=last";
      fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (input.value.trim() === q) render(data, q);
        })
        .catch(function () { /* aborted or offline — keep current results */ });
    }, 250);
  });
  /* Enter submits the wrapping form → /search?q=… (explicit escape hatch) */
})();

/* ── Custom cursor ── */
if (isDesktop && window.matchMedia("(hover: hover)").matches) {
  const cursor = document.getElementById("cursor");
  const label = document.getElementById("cursorLabel");
  const setX = gsap.quickTo(cursor, "x", { duration: 0.35, ease: "power3" });
  const setY = gsap.quickTo(cursor, "y", { duration: 0.35, ease: "power3" });
  window.addEventListener("pointermove", (e) => {
    setX(e.clientX - 7);
    setY(e.clientY - 7);
  }, { passive: true });

  document.querySelectorAll("[data-cursor]").forEach((el) => {
    el.addEventListener("pointerenter", () => {
      label.textContent = el.dataset.cursor;
      cursor.classList.add("is-hover");
    });
    el.addEventListener("pointerleave", () => cursor.classList.remove("is-hover"));
  });
} else {
  const c = document.getElementById("cursor");
  if (c) c.remove();
}
